// @flow

import { get as _get } from 'lodash';
import * as React from 'react';

import { Button, ButtonIcon, Footer, Page, PageContainer, Panel } from '@sonyged/ext-proc';

import { type Crust, Pizza, type Sauce, type Topping, Toppings, ingredientIcon, ingredientName } from './domain/pizza';
import { Customer } from './domain/customer';
import type { FinishedRound, NewRound } from './domain/Round';
import { PizzaIngredientIcon } from './icons/PizzaIngredientIcon';
import PizzaCustomerIcon from './icons/PizzaCustomerIcon';
import ProcPizzaSelect, { type SelectOption } from './ProcPizzaSelect';

const crustOptions: SelectOption<Crust>[] = [
  {
    value: 'thin_crust',
    label: ingredientName('thin_crust'),
    image: ingredientIcon('thin_crust'),
  },
  {
    value: 'normal_crust',
    label: ingredientName('normal_crust'),
    image: ingredientIcon('normal_crust'),
  },
  {
    value: 'thick_crust',
    label: ingredientName('thick_crust'),
    image: ingredientIcon('thick_crust'),
  },
];

const sauceOptions: SelectOption<Sauce>[] = [
  {
    value: 'tomato_sauce',
    label: ingredientName('tomato_sauce'),
    image: ingredientIcon('tomato_sauce'),
  },
  {
    value: 'white_sauce',
    label: ingredientName('white_sauce'),
    image: ingredientIcon('white_sauce'),
  },
  {
    value: 'basil_sauce',
    label: ingredientName('basil_sauce'),
    image: ingredientIcon('basil_sauce'),
  },
];

const toppingOptions: SelectOption<Topping>[] = [
  {
    value: Toppings.crab,
    label: ingredientName(Toppings.crab),
    image: ingredientIcon(Toppings.crab),
  },
  {
    value: Toppings.bacon,
    label: ingredientName(Toppings.bacon),
    image: ingredientIcon(Toppings.bacon),
  },
  {
    value: Toppings.tomato,
    label: ingredientName(Toppings.tomato),
    image: ingredientIcon(Toppings.tomato),
  },
  {
    value: Toppings.teriyaki_chicken,
    label: ingredientName(Toppings.teriyaki_chicken),
    image: ingredientIcon(Toppings.teriyaki_chicken),
  },
  {
    value: Toppings.squid,
    label: ingredientName(Toppings.squid),
    image: ingredientIcon(Toppings.squid),
  },
  {
    value: Toppings.salami,
    label: ingredientName(Toppings.salami),
    image: ingredientIcon(Toppings.salami),
  },
  {
    value: Toppings.bell_peeper,
    label: ingredientName(Toppings.bell_peeper),
    image: ingredientIcon(Toppings.bell_peeper),
  },
  {
    value: Toppings.shrimp,
    label: ingredientName(Toppings.shrimp),
    image: ingredientIcon(Toppings.shrimp),
  },
  {
    value: Toppings.corn,
    label: ingredientName(Toppings.corn),
    image: ingredientIcon(Toppings.corn),
  },
];

type Props = {
  round: NewRound,
  pastRound: ?FinishedRound,
  onNext: FinishedRound => void,
  onBack?: () => void,
  changable: 'all' | 'topping' | 'crust' | 'sauce',
  mode: 'research' | 'challenge',
};

type State = {
  crust: ?Crust,
  sauce: ?Sauce,
  toppings: Array<?Topping>,
  talkingCustomer: ?Customer,
};

class PizzaBuilderView extends React.Component<Props, State> {
  static defaultProps = {
    mode: 'challenge',
    changable: 'all',
    pastRound: null,
  };

  ingredientInputs: Array<?HTMLDivElement>;

  constructor(props: Props) {
    super(props);

    this.ingredientInputs = new Array(5).fill(null);

    let toppings: Array<?Topping> = [null, null, null];
    if (this.props.pastRound) {
      toppings = [];
      this.props.pastRound.pizza.toppings.forEach(topping => {
        toppings.push(topping);
      });
    }

    this.state = {
      crust: this.props.pastRound ? this.props.pastRound.pizza.crust : null,
      sauce: this.props.pastRound ? this.props.pastRound.pizza.sauce : null,
      toppings,
      talkingCustomer: _get(props.round.customers, '0'),
    };

    (this: any).handleCreateClicked = this.handleCreateClicked.bind(this);
    (this: any).setCrust = this.setCrust.bind(this);
    (this: any).setSauce = this.setSauce.bind(this);
    (this: any).setTalkingCustomer = this.setTalkingCustomer.bind(this);
  }

  componentDidMount() {
    const input = this.ingredientInputs.find(Boolean);
    if (input) {
      input.focus();
    }
  }

  handleCreateClicked() {
    if (this.state.crust && this.state.sauce) {
      const pizza = new Pizza(this.state.crust, this.state.sauce, this.state.toppings.filter(Boolean));
      this.props.onNext({
        ...this.props.round,
        pizza,
      });
    }
  }

  setCrust(crust: Crust) {
    this.setState({ crust });
  }

  setSauce(sauce: Sauce) {
    this.setState({ sauce });
  }

  setTopping(index: 0 | 1 | 2, topping: Topping) {
    const toppings = this.state.toppings.slice(0); // clone
    toppings[index] = topping;
    this.setState({ toppings });
  }

  setTalkingCustomer(customer: Customer) {
    this.setState({ talkingCustomer: customer });
  }

  canMakePizza() {
    const { crust, sauce, toppings } = this.state;
    return Boolean(crust && sauce && toppings.filter(Boolean).length === 3);
  }

  render() {
    const allChangable = this.props.changable === 'all';
    const crustChangable = allChangable || this.props.changable === 'crust';
    const sauceChangable = allChangable || this.props.changable === 'sauce';
    const toppingChangable = allChangable || this.props.changable === 'topping';

    const { crust, sauce, toppings } = this.state;
    const ingredients = [];
    if (crust) ingredients.push(crust);
    if (sauce) ingredients.push(sauce);
    ingredients.push(...toppings.filter(Boolean));

    if (ingredients.length < 5) {
      ingredients.push(...new Array(5 - ingredients.length).fill(null));
    }

    const infoColor = this.props.mode === 'challenge' ? 'tertiary100-bg-color' : 'gray5-bg-color';

    return (
      <div className="pizza-inner-view pizza-builder-view">
        <Page.fluid className={`${infoColor}  ${this.props.mode} pizza-customerInfo`} fullHeight={false}>
          <PageContainer.small className="pizza-customerInfo-container">
            <div className="pizza-customers">
              {this.props.round.customers[0] && (
                <PizzaCustomerIcon
                  talking={this.state.talkingCustomer === this.props.round.customers[0]}
                  onRequestToTalk={this.setTalkingCustomer}
                  customer={this.props.round.customers[0]}
                />
              )}
              {this.props.round.customers[1] && (
                <PizzaCustomerIcon
                  talking={this.state.talkingCustomer === this.props.round.customers[1]}
                  onRequestToTalk={this.setTalkingCustomer}
                  customer={this.props.round.customers[1]}
                />
              )}
              {this.props.round.customers[2] && (
                <PizzaCustomerIcon
                  talking={this.state.talkingCustomer === this.props.round.customers[2]}
                  onRequestToTalk={this.setTalkingCustomer}
                  customer={this.props.round.customers[2]}
                />
              )}
            </div>
          </PageContainer.small>
        </Page.fluid>

        <Page.fluid menu={false} fullHeight={false} className="gray10-bg-color flex1">
          <PageContainer.small>
            <Panel.vertical className="pizza-builder-form">
              <span className="pizza-builder-form-title">ピザを つくろう</span>
              <div className="ingredient-icons">
                {ingredients.map((ingredient, i) => {
                  return <PizzaIngredientIcon ingredient={ingredient} key={i} />;
                })}
              </div>
              <table className={'proc-table'}>
                <thead>
                  <tr>
                    <th width={'50%'}>ピザきじ</th>
                    <th width={'50%'}>ソース</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={'value'}>
                      {crustChangable || !this.props.pastRound ? (
                        <ProcPizzaSelect
                          innerRef={r => {
                            this.ingredientInputs[0] = r;
                          }}
                          tabIndex={0}
                          value={this.state.crust}
                          options={crustOptions}
                          onSelect={this.setCrust}
                        />
                      ) : (
                        ingredientName(this.props.pastRound.pizza.crust)
                      )}
                    </td>
                    <td className={'value'}>
                      {sauceChangable || !this.props.pastRound ? (
                        <ProcPizzaSelect
                          innerRef={r => {
                            this.ingredientInputs[1] = r;
                          }}
                          tabIndex={0}
                          value={this.state.sauce}
                          options={sauceOptions}
                          onSelect={this.setSauce}
                        />
                      ) : (
                        ingredientName(this.props.pastRound.pizza.sauce)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className={'proc-table'}>
                <thead>
                  <tr>
                    <th colSpan={2}>トッピング</th>
                  </tr>
                </thead>
                <tbody>
                  {toppingChangable || !this.props.pastRound ? (
                    <React.Fragment>
                      <tr>
                        <td width="50%">
                          <ProcPizzaSelect
                            innerRef={r => {
                              this.ingredientInputs[2] = r;
                            }}
                            tabIndex={0}
                            value={this.state.toppings[0]}
                            options={toppingOptions}
                            onSelect={topping => this.setTopping(0, topping)}
                          />
                        </td>
                        <td width="50%">
                          <ProcPizzaSelect
                            innerRef={r => {
                              this.ingredientInputs[3] = r;
                            }}
                            tabIndex={0}
                            value={this.state.toppings[1]}
                            options={toppingOptions}
                            onSelect={topping => this.setTopping(1, topping)}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <ProcPizzaSelect
                            innerRef={r => {
                              this.ingredientInputs[4] = r;
                            }}
                            tabIndex={0}
                            value={this.state.toppings[2]}
                            options={toppingOptions}
                            onSelect={topping => this.setTopping(2, topping)}
                          />
                        </td>
                        <td />
                      </tr>
                    </React.Fragment>
                  ) : (
                    <tr>
                      <td className="value" colSpan={2}>
                        {this.props.pastRound.pizza.toppings.map(ingredientName).join()}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Panel.vertical>
          </PageContainer.small>
        </Page.fluid>
        <Footer.action className="white-bg-color">
          {this.props.onBack ? (
            <div className="left">
              <Button.normal onClick={this.props.onBack}>
                <ButtonIcon.back />
              </Button.normal>
            </div>
          ) : null}
          <Button.attention
            disabled={!this.canMakePizza()}
            className={this.canMakePizza() ? '' : 'proc-button--disabled'}
            onClick={this.canMakePizza() ? this.handleCreateClicked : null}
          >
            <ButtonIcon.action />
          </Button.attention>
        </Footer.action>
      </div>
    );
  }
}

export default PizzaBuilderView;
